"use client";

import {
  IconBell,
  IconBooks,
  IconChevronDown,
  IconCode,
  IconComponents,
  IconHistory,
  IconPhoto,
  IconRocket,
  IconRoute,
  IconShieldCheck,
  IconShieldHeart,
  IconTerminal2,
  IconUsers,
} from "@tabler/icons-react";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import PreviewLabel from "src/components/common/PreviewLabel/PreviewLabel";
import SidebarNavigation from "src/components/common/SidebarNavigation/SidebarNavigation";
import { DOCS_NAVIGATION } from "src/lib/DocsNavigation";
import { usePathname } from "src/lib/Navigation";
import { useExtracted } from "#i18n";
import styles from "./DocsSidebar.module.scss";

export interface DocsSidebarProps {
  className?: string;
}

const groupIcons = {
  start: IconRocket,
  guides: IconRoute,
  widgets: IconComponents,
  moderation: IconShieldCheck,
  policies: IconShieldHeart,
  assets: IconPhoto,
  events: IconBell,
  tools: IconTerminal2,
  projects: IconUsers,
  api: IconCode,
  sdk: IconBooks,
};

function NavigationGroup({
  group,
  pathname,
}: {
  group: (typeof DOCS_NAVIGATION)[number];
  pathname: string;
}) {
  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (group.pages.some((page) => page.route === pathname)) setOpen(true);
  }, [group, pathname]);
  const Icon = groupIcons[group.id];
  return (
    <SidebarNavigation.Group>
      <SidebarNavigation.Item
        icon={<Icon size={17} stroke={1.8} aria-hidden />}
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={`docs-nav-${group.id}`}
        trailing={
          <IconChevronDown
            size={16}
            aria-hidden
            style={{ transform: open ? undefined : "rotate(-90deg)" }}
          />
        }
      >
        {group.title}
      </SidebarNavigation.Item>
      {open && (
        <SidebarNavigation.List id={`docs-nav-${group.id}`}>
          {group.pages.map((page) => (
            <SidebarNavigation.Item
              key={page.route}
              href={page.route}
              selected={pathname === page.route}
              depth={page.route.startsWith("/docs/sdk/") && page.route.endsWith("/testing") ? 2 : 1}
              trailing={page.preview ? <PreviewLabel feature={page.preview} /> : undefined}
            >
              {page.title}
            </SidebarNavigation.Item>
          ))}
        </SidebarNavigation.List>
      )}
    </SidebarNavigation.Group>
  );
}

export default function DocsSidebar({ className }: DocsSidebarProps) {
  const t = useExtracted();
  const pathname = usePathname();
  const navigationRef = useRef<HTMLElement>(null);
  const previousPathname = useRef(pathname);

  useEffect(() => {
    // Leave the server-rendered sidebar in place on hydration; reveal only after navigation.
    if (previousPathname.current === pathname) return;
    previousPathname.current = pathname;
    const navigation = navigationRef.current;
    if (!navigation) return;
    const revealSelectedPage = () => {
      const selected = navigation.querySelector<HTMLElement>('[aria-current="page"]');
      if (!selected || !navigation.clientHeight) return;
      const viewport = navigation.getBoundingClientRect();
      const item = selected.getBoundingClientRect();
      // Scroll only the sidebar, leaving the article and its anchor position alone.
      if (item.top < viewport.top) navigation.scrollTop += item.top - viewport.top;
      else if (item.bottom > viewport.bottom) navigation.scrollTop += item.bottom - viewport.bottom;
    };
    const frame = requestAnimationFrame(revealSelectedPage);
    return () => cancelAnimationFrame(frame);
  }, [pathname]);

  return (
    <SidebarNavigation
      ref={navigationRef}
      className={clsx(styles.navigation, className)}
      aria-label={t("Documentation navigation")}
    >
      {DOCS_NAVIGATION.map((group) => (
        <NavigationGroup key={group.id} group={group} pathname={pathname} />
      ))}
      <SidebarNavigation.Item
        href="/docs/changelog"
        selected={pathname === "/docs/changelog"}
        icon={<IconHistory size={17} stroke={1.8} aria-hidden />}
      >
        {t("Changelog")}
      </SidebarNavigation.Item>
    </SidebarNavigation>
  );
}
